import {
  useCertificationCatalogue,
  useDeleteResourceCertification,
  useDeleteResourceCertificationAttachment,
  useSetResourceCertification,
  useUploadResourceCertificationAttachment,
} from '@my/directory-api/hooks';
import type {
  AppNameValue,
  CertificationCatalogueEntry,
  Resource,
  ResourceCertification,
  TagComplianceActionValue,
} from '@my/directory-api';
import type { ServiceTag } from '@my/tms-api';
import { showToast } from '@my/ui';
import { Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentCell } from './AttachmentCell';
import {
  certKeyToSnake,
  filterCatalogueByType,
  snakeToCertKey,
} from './lib/resourceCertHelpers';

interface CertificationsSectionProps {
  resource: Resource;
  organisationId: string;
  /**
   * Org-wide service-tag library, pre-filtered to tags whose
   * `applyToServiceTypes` opts into the Resources area. Rendered as a
   * read-only chip row — operators select from the list, no inline
   * create. New tags are configured in Central Configuration; this
   * picker reflects whatever is there.
   *
   * The cert table stores `customLabel` (tag name) + `app = 'Tms'` per
   * applied tag, so the link is name-based — there's no FK to the
   * ServiceTag table (cross-bounded-context: Resource lives in
   * My.Directory, ServiceTag in My.Tms). Renaming a tag in Central
   * Config will leave already-applied certs reading the old name; the
   * operator can re-add the tag to bring the cert in line. Acceptable
   * trade-off vs. introducing a logical FK across contexts.
   */
  serviceTags?: ServiceTag[];
  /** Disables the picker + grid while a parent-level mutation is in flight. */
  isBusy?: boolean;
}

/**
 * Owning app for Custom certs created on this page. The TMS Resources
 * page only ever creates / displays Custom certs scoped to TMS — Depot
 * and Expenses surface their own Custom rows on their own Resources
 * pages, filtered by their own `App` value.
 */
const OWNING_APP: AppNameValue = 'Tms';

const COMPLIANCE_ACTIONS: TagComplianceActionValue[] = ['Stop', 'Warn', 'None'];

const COMPLIANCE_ACTIVE_CLASS: Record<TagComplianceActionValue, string> = {
  Stop: 'bg-red-500 border-red-500 text-white',
  Warn: 'bg-yellow-400 border-yellow-400 text-slate-900',
  None: 'bg-green-500 border-green-500 text-white',
};

/**
 * Cert picker + grid for a single resource.
 *
 * UX: live-update — every cert mutation (toggle, expiry change,
 * compliance action click, attachment upload) is an immediate API
 * call. Text inputs save-on-blur; chip clicks fire on click;
 * attachments fire on drop. There's no "Save" button on this section
 * — operators don't have a discard-changes mental model for cert
 * tweaks; they expect each tweak to land.
 *
 * Compliance roll-up on the parent resource updates BE-side after
 * every cert mutation; the resource list refetches via React Query
 * invalidation so the dot colour + nextExpiryDate refresh in place.
 *
 * Two cert kinds:
 *   - Predefined (Adr, Cpc, Mot, …) — driven by the catalogue.
 *     Chips toggle one-per-(resource, key); applies-to filtering hides
 *     "MOT on driver" etc.
 *   - Custom — free-form, app-scoped (TMS-only on this page). Operator
 *     types a label ("Service Tag", "Annual Inspection", …) and the
 *     row joins the same grid; the label is editable per row, and
 *     deduplication is on `(resource, app, lower(label))` BE-side.
 */
export function CertificationsSection({
  resource,
  organisationId,
  serviceTags = [],
  isBusy: parentBusy = false,
}: CertificationsSectionProps) {
  const { t } = useTranslation(['resources', 'common']);

  const catalogueQuery = useCertificationCatalogue();
  const setCertMutation = useSetResourceCertification();
  const deleteCertMutation = useDeleteResourceCertification();
  const uploadAttachmentMutation = useUploadResourceCertificationAttachment();
  const deleteAttachmentMutation = useDeleteResourceCertificationAttachment();

  // Cert-only saving signal — drives the "Saving…" pill in the header.
  // We DON'T include parentBusy here: while the parent resource form is
  // saving, the indicator next to "Certifications & Compliance" would
  // be misleading (no cert is being persisted at that moment).
  const isCertSaving =
    setCertMutation.isPending ||
    deleteCertMutation.isPending ||
    uploadAttachmentMutation.isPending ||
    deleteAttachmentMutation.isPending;

  // Combined disable signal for inputs / chips. Includes parentBusy so
  // operators can't kick off a cert mutation mid-resource-save (which
  // could land out-of-order vs. the resource update).
  const isBusy = parentBusy || isCertSaving;

  // Split active certs: predefined slot uniqueness is by key, custom
  // slot is per-(app, lower(label)) and only this app's customs are
  // shown so other apps' service-tag-style rows don't pollute the
  // grid.
  const predefinedCerts = useMemo(
    () => resource.certifications.filter((c) => c.certificationKey !== 'Custom'),
    [resource.certifications],
  );
  const customCerts = useMemo(
    () =>
      resource.certifications.filter(
        (c) => c.certificationKey === 'Custom' && c.app === OWNING_APP,
      ),
    [resource.certifications],
  );

  // Index predefined certs by their catalogue snake_case key for the
  // picker toggle check. Skips deleted rows defensively (the API
  // filters them server-side, but defending against stale optimistic
  // state).
  const activeCertByKey = useMemo(() => {
    const map = new Map<string, ResourceCertification>();
    for (const cert of predefinedCerts) {
      map.set(certKeyToSnake(cert.certificationKey), cert);
    }
    return map;
  }, [predefinedCerts]);

  // Index this resource's custom certs by lower-cased label for the
  // service-tag chip toggle check. Lower-cased to match the BE's
  // case-insensitive dedupe — clicking "Annual Inspection" when the
  // resource already has "annual inspection" must register as active.
  const activeCustomByLabel = useMemo(() => {
    const map = new Map<string, ResourceCertification>();
    for (const cert of customCerts) {
      if (cert.customLabel) {
        map.set(cert.customLabel.trim().toLowerCase(), cert);
      }
    }
    return map;
  }, [customCerts]);

  // Filter the catalogue to certs that apply to this resource type.
  // Country filter is null for now — the FE-side resource doesn't
  // carry a country yet (the BE catalogue's `countries` field is in
  // place for when it does).
  const availableCerts = useMemo(
    () => filterCatalogueByType(catalogueQuery.data, resource.type, null),
    [catalogueQuery.data, resource.type],
  );

  // Set of normalised names for every predefined cert in the catalogue.
  // Both the snake_case key ("driver_card") and the human label
  // ("Driver Card") get normalised to the same comparable form so a
  // service tag with EITHER spelling resolves as "this collides with a
  // predefined cert, hide it from the picker." Normalisation: lower-
  // case, strip everything that isn't [a-z0-9].
  const predefinedNameSet = useMemo(() => {
    const set = new Set<string>();
    for (const c of catalogueQuery.data?.certifications ?? []) {
      set.add(normaliseTagName(c.key));
      set.add(normaliseTagName(c.label));
    }
    return set;
  }, [catalogueQuery.data]);

  // Hide service tags whose name collides with a predefined cert —
  // operators apply ADR via the predefined "ADR" chip, not via a
  // duplicated "adr" service tag. Avoids two cert rows for the same
  // semantic concept.
  const visibleServiceTags = useMemo(
    () => serviceTags.filter((tag) => !predefinedNameSet.has(normaliseTagName(tag.name))),
    [serviceTags, predefinedNameSet],
  );

  // ── Toggle / mutate handlers ─────────────────────────────────────────────

  const togglePredefined = async (entry: CertificationCatalogueEntry) => {
    const existing = activeCertByKey.get(entry.key);
    try {
      if (existing) {
        await deleteCertMutation.mutateAsync({
          resourceId: resource.id,
          certificationId: existing.id,
          organisationId,
        });
      } else {
        await setCertMutation.mutateAsync({
          body: {
            resourceId: resource.id,
            certificationKey: snakeToCertKey(entry.key),
            customLabel: null,
            app: null,
            expiryDate: null,
            warnDays: null,
            complianceAction: 'None',
            note: null,
          },
          organisationId,
        });
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update certification.',
      });
    }
  };

  // Click a service-tag chip — add the tag to this resource if missing,
  // remove it if already applied. The tag's canonical name is stored on
  // the cert as `customLabel` (the link is name-based, no FK to the
  // ServiceTag table). Active match is case-insensitive to mirror the
  // BE's dedupe semantics.
  const toggleServiceTag = async (tag: ServiceTag) => {
    const existing = activeCustomByLabel.get(tag.name.trim().toLowerCase());
    try {
      if (existing) {
        await deleteCertMutation.mutateAsync({
          resourceId: resource.id,
          certificationId: existing.id,
          organisationId,
        });
      } else {
        await setCertMutation.mutateAsync({
          body: {
            resourceId: resource.id,
            certificationKey: 'Custom',
            customLabel: tag.name,
            app: OWNING_APP,
            expiryDate: null,
            warnDays: null,
            complianceAction: 'None',
            note: null,
          },
          organisationId,
        });
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update service tag.',
      });
    }
  };

  // Generic patch — for Custom certs we MUST forward customLabel + app
  // alongside the patch (the BE validator requires both whenever
  // CertificationKey === Custom; omitting them would 400). The label
  // itself is no longer mutable through the row (it's pinned to the
  // service tag's name in Central Config) — we always forward the
  // existing cert.customLabel verbatim.
  const updateCert = async (
    cert: ResourceCertification,
    patch: {
      expiryDate?: string | null;
      warnDays?: number | null;
      complianceAction?: TagComplianceActionValue;
      note?: string | null;
    },
  ) => {
    try {
      const isCustom = cert.certificationKey === 'Custom';
      await setCertMutation.mutateAsync({
        body: {
          resourceId: cert.resourceId,
          certificationKey: cert.certificationKey,
          customLabel: isCustom ? cert.customLabel : null,
          app: isCustom ? cert.app : null,
          expiryDate: 'expiryDate' in patch ? patch.expiryDate : cert.expiryDate,
          warnDays: 'warnDays' in patch ? patch.warnDays : cert.warnDays,
          complianceAction:
            'complianceAction' in patch ? patch.complianceAction : cert.complianceAction,
          note: 'note' in patch ? patch.note : cert.note,
        },
        organisationId,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update certification.',
      });
    }
  };

  const uploadAttachment = async (cert: ResourceCertification, file: File) => {
    try {
      await uploadAttachmentMutation.mutateAsync({
        resourceId: cert.resourceId,
        certificationId: cert.id,
        file,
        organisationId,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to upload attachment.',
      });
    }
  };

  const removeAttachment = async (cert: ResourceCertification) => {
    try {
      await deleteAttachmentMutation.mutateAsync({
        resourceId: cert.resourceId,
        certificationId: cert.id,
        organisationId,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to remove attachment.',
      });
    }
  };

  // Catalogue label lookup for the cert grid header per row.
  const labelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of catalogueQuery.data?.certifications ?? []) map.set(c.key, c.label);
    return map;
  }, [catalogueQuery.data]);

  // Pre-resolve compliance action labels (typed-keys won't accept
  // template literals; same pattern as the rest of the page).
  const actionLabels: Record<TagComplianceActionValue, string> = {
    Stop: t('certifications.actions.stop'),
    Warn: t('certifications.actions.warn'),
    None: t('certifications.actions.none'),
  };

  // Predefined first, then customs — operators expect the predefined
  // (compliance-relevant) certs near the top of the grid.
  const orderedCerts = useMemo(
    () => [...predefinedCerts, ...customCerts],
    [predefinedCerts, customCerts],
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Predefined certs picker chip row */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-600">{t('certifications.title')}</p>
          {/* Saving pill — sits aria-live so screen readers announce
              the transient state. Visible only while a cert mutation
              is in flight; the BE's two-stage SaveChanges (cert row,
              then resource compliance recompute) can take a beat, so
              the pill is the operator's signal that the keystroke /
              click did land. */}
          {isCertSaving && (
            <span
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('certifications.saving')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {availableCerts.map((entry) => {
            const isActive = activeCertByKey.has(entry.key);
            return (
              <button
                key={entry.key}
                type="button"
                onClick={() => togglePredefined(entry)}
                disabled={isBusy}
                title={entry.description}
                className={chipClass(isActive)}
              >
                {entry.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Service Tags chip row — read-only library, configured in
          Central Configuration. Each chip toggles application of the
          tag to this resource (active = applied). New tags / rename /
          delete are managed in Central Configuration; this picker
          mirrors whatever's there.
          The chip's tooltip surfaces the tag's description (when set)
          so operators can see what the tag is for without leaving the
          page. */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-600">
          {t('certifications.serviceTags')}
        </p>
        {visibleServiceTags.length === 0 ? (
          <p className="text-[10px] italic text-slate-400">
            {t('certifications.noServiceTagsConfigured')}
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {visibleServiceTags.map((tag) => {
              const isActive = activeCustomByLabel.has(tag.name.trim().toLowerCase());
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleServiceTag(tag)}
                  disabled={isBusy}
                  title={tag.description ?? undefined}
                  className={tagChipClass(isActive)}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Grid — one row per active cert (predefined first, customs after). */}
      {orderedCerts.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-500">
              <tr>
                <th className="w-[140px] px-2 py-1.5 text-left font-medium">
                  {t('certifications.title')}
                </th>
                <th className="w-[130px] px-2 py-1.5 text-left font-medium">
                  {t('certifications.expiryDate')}
                </th>
                <th className="w-[80px] px-2 py-1.5 text-left font-medium">
                  {t('certifications.warnDays')}
                </th>
                <th className="w-[140px] px-2 py-1.5 text-left font-medium">
                  {t('certifications.complianceAction')}
                </th>
                <th className="w-[140px] px-2 py-1.5 text-left font-medium">
                  {t('certifications.attachment')}
                </th>
                <th className="px-2 py-1.5 text-left font-medium">
                  {t('certifications.note')}
                </th>
                <th className="w-[28px]" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {orderedCerts.map((cert) => {
                const isCustom = cert.certificationKey === 'Custom';
                const label = isCustom
                  ? (cert.customLabel ?? t('certifications.customLabelFallback'))
                  : (labelByKey.get(certKeyToSnake(cert.certificationKey)) ??
                    cert.certificationKey);
                return (
                  <CertificationRow
                    key={cert.id}
                    cert={cert}
                    label={label}
                    isCustom={isCustom}
                    actionLabels={actionLabels}
                    isBusy={isBusy}
                    onUpdate={(patch) => updateCert(cert, patch)}
                    onUploadAttachment={(file) => uploadAttachment(cert, file)}
                    onRemoveAttachment={() => removeAttachment(cert)}
                    onRemove={async () => {
                      try {
                        await deleteCertMutation.mutateAsync({
                          resourceId: cert.resourceId,
                          certificationId: cert.id,
                          organisationId,
                        });
                      } catch (err) {
                        showToast({
                          type: 'error',
                          message:
                            err instanceof Error
                              ? err.message
                              : 'Failed to remove certification.',
                        });
                      }
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── CertificationRow ────────────────────────────────────────────────────────

interface CertificationRowProps {
  cert: ResourceCertification;
  label: string;
  /**
   * When true, the label chip uses the amber palette to read as a
   * service-tag-driven row (vs. the sky palette for predefined certs).
   * The label itself is read-only — service-tag names are configured
   * in Central Configuration; renaming there is the only path.
   */
  isCustom: boolean;
  actionLabels: Record<TagComplianceActionValue, string>;
  isBusy: boolean;
  onUpdate: (patch: {
    expiryDate?: string | null;
    warnDays?: number | null;
    complianceAction?: TagComplianceActionValue;
    note?: string | null;
  }) => void | Promise<void>;
  onUploadAttachment: (file: File) => void | Promise<void>;
  onRemoveAttachment: () => void | Promise<void>;
  onRemove: () => void | Promise<void>;
}

/**
 * A single row in the cert grid. Local state for text-ish inputs
 * (label, expiry, warn days, note) so the operator can type freely;
 * we save on blur (natural commit point — they're done editing once
 * they tab or click away). Compliance action chips and the remove
 * button save immediately on click.
 */
function CertificationRow({
  cert,
  label,
  isCustom,
  actionLabels,
  isBusy,
  onUpdate,
  onUploadAttachment,
  onRemoveAttachment,
  onRemove,
}: CertificationRowProps) {
  // Local state for text inputs — synced FROM cert on prop change so
  // the row reflects server-side state after a refetch. Save-on-blur
  // calls onUpdate with the current local value.
  const [expiry, setExpiry] = useState<string>(cert.expiryDate ?? '');
  const [warnDays, setWarnDays] = useState<string>(
    cert.warnDays != null ? String(cert.warnDays) : '',
  );
  const [note, setNote] = useState<string>(cert.note ?? '');

  // Re-sync when the cert prop updates (e.g. another mutation invalidated
  // the parent query and we got fresh server state).
  useEffect(() => setExpiry(cert.expiryDate ?? ''), [cert.expiryDate]);
  useEffect(
    () => setWarnDays(cert.warnDays != null ? String(cert.warnDays) : ''),
    [cert.warnDays],
  );
  useEffect(() => setNote(cert.note ?? ''), [cert.note]);

  const commitExpiry = () => {
    const next = expiry.trim() || null;
    if (next !== (cert.expiryDate ?? null)) void onUpdate({ expiryDate: next });
  };
  const commitWarnDays = () => {
    const trimmed = warnDays.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && (Number.isNaN(next) || next < 1 || next > 730)) return; // BE rejects
    if (next !== (cert.warnDays ?? null)) void onUpdate({ warnDays: next });
  };
  const commitNote = () => {
    const next = note.trim() || null;
    if (next !== (cert.note ?? null)) void onUpdate({ note: next });
  };

  return (
    <tr>
      <td className="px-2 py-1.5">
        {/* Read-only chip — predefined certs use the sky palette, custom
            (service-tag-driven) certs use amber so the operator can see
            at a glance which rows trace back to a Central-Config tag. */}
        <span
          className={
            isCustom
              ? 'inline-flex items-center self-start rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800'
              : 'inline-flex items-center self-start rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700'
          }
        >
          {label}
        </span>
      </td>

      <td className="px-2 py-1">
        <input
          type="date"
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
          onBlur={commitExpiry}
          disabled={isBusy}
          className="w-full rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100"
        />
      </td>

      <td className="px-2 py-1">
        <input
          type="number"
          min={1}
          max={730}
          value={warnDays}
          placeholder="30"
          onChange={(e) => setWarnDays(e.target.value)}
          onBlur={commitWarnDays}
          disabled={isBusy}
          className="w-full rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100"
        />
      </td>

      <td className="px-2 py-1">
        <div className="flex gap-1">
          {COMPLIANCE_ACTIONS.map((action) => {
            const active = cert.complianceAction === action;
            return (
              <button
                key={action}
                type="button"
                onClick={() => onUpdate({ complianceAction: action })}
                disabled={isBusy}
                className={`rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50 ${
                  active
                    ? COMPLIANCE_ACTIVE_CLASS[action]
                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400'
                }`}
              >
                {actionLabels[action]}
              </button>
            );
          })}
        </div>
      </td>

      <td className="px-2 py-1">
        <AttachmentCell
          cert={cert}
          isBusy={isBusy}
          onUpload={onUploadAttachment}
          onRemove={onRemoveAttachment}
        />
      </td>

      <td className="px-2 py-1">
        <input
          type="text"
          value={note}
          placeholder="Note…"
          onChange={(e) => setNote(e.target.value)}
          onBlur={commitNote}
          disabled={isBusy}
          maxLength={1024}
          className="w-full rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100"
        />
      </td>

      <td className="px-1 py-1 text-center">
        <button
          type="button"
          onClick={onRemove}
          disabled={isBusy}
          aria-label={`Remove ${label}`}
          className="text-slate-300 transition-colors hover:text-red-400 disabled:opacity-50"
        >
          <X className="h-3 w-3" />
        </button>
      </td>
    </tr>
  );
}

/**
 * Lower-case + strip all non-alphanumeric characters. Used to compare
 * service-tag names against the predefined cert catalogue so spellings
 * like "ADR", "adr", "A.D.R.", "A-D-R" all resolve as a collision with
 * the predefined `adr` cert key. Same normalisation applies to both
 * sides of the comparison so the equivalence is symmetric.
 */
function normaliseTagName(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function chipClass(active: boolean): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  return `${base} ${
    active
      ? 'border-sky-700 bg-sky-700 text-white'
      : 'border-slate-300 bg-white text-slate-600 hover:border-slate-500 hover:text-slate-800'
  }`;
}

/**
 * Service-tag chip — amber palette so they read as the second-class
 * cert kind (predefined certs are sky-blue, service tags are amber).
 * Same active / inactive semantics as the predefined `chipClass` above.
 */
function tagChipClass(active: boolean): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  return `${base} ${
    active
      ? 'border-amber-600 bg-amber-600 text-white'
      : 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500 hover:text-amber-950'
  }`;
}
